import type { PowerBiReportInternalCode } from 'walden-fast/types/fast-backend/power-bi';
import type { FilterValues } from 'walden-fast/types/filters';
import config from 'walden-fast/config/environment';

export const INTERNAL_CODE_FILTER_TABLE_MAPPING: Record<
  PowerBiReportInternalCode,
  string[]
> = {
  Warehousing: ['Clients', 'Items'],
  Order2Cash: ['Clients', 'Items', 'Customers', 'General Ledger Accounts'],
  Transport: ['Clients'],
  OrderTracking: ['Clients', 'Items', 'Customers'],
  WaldenML: ['Clients'],
  Orchestration: ['Clients'],
  KPI1: ['Clients', 'Items'],
  KPI2: ['Clients', 'Items'],
  KPI3: ['Clients'],
  KPI4: ['Clients'],
  KPI5: ['Clients', 'Items'],
  KPI6: ['Clients'],
  WaldenGreen: ['Clients'],
};

/**
 * Parse LRD setup ClientNameMaster codes from environment config
 */
function getLrdSetupClientNameMasters(): Set<string> {
  const lrdClientsString = config.lrdSetupClientNameMasters || '';
  return new Set(
    lrdClientsString
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code.length > 0),
  );
}

/**
 * Extract unique clientNameMaster codes from filters
 */
function getUniqueClientNameMasters(
  filters: FilterValues | null | undefined,
): Set<string> {
  if (!filters?.clients || filters.clients.length === 0) {
    return new Set();
  }

  const clientNameMasters = filters.clients
    .map((client) => client.externalIdentifiers?.powerBI?.clientNameMaster)
    .filter((name): name is string => Boolean(name));

  return new Set(clientNameMasters);
}

/**
 * Check if any of the filter clients has LRD setup
 */
function hasLrdSetup(filters: FilterValues | null | undefined): boolean {
  const clientNameMasters = getUniqueClientNameMasters(filters);
  if (clientNameMasters.size === 0) {
    return false;
  }

  const lrdClients = getLrdSetupClientNameMasters();
  if (lrdClients.size === 0) {
    return false;
  }

  // Check if any clientNameMaster is in the LRD setup list
  for (const clientNameMaster of clientNameMasters) {
    if (lrdClients.has(clientNameMaster)) {
      return true;
    }
  }

  return false;
}

/**
 * Get filter tables for a report, excluding Items table for LRD clients when applicable
 */
export function getFilterTables(
  internalCode: PowerBiReportInternalCode,
  filters: FilterValues | null | undefined,
): string[] {
  const tables = [...INTERNAL_CODE_FILTER_TABLE_MAPPING[internalCode]];

  // For Order2Cash report with LRD setup clients, remove Items table from filtering
  if (internalCode === 'Order2Cash' && hasLrdSetup(filters)) {
    return tables.filter((table) => table !== 'Items');
  }

  return tables;
}
